/**
 * Промпт на `/generate`: «Авто» (сервис собирает из правил) или «Мой» (свой текст вместо задания и
 * правил; «Что учесть» и список входов пары сервис дописывает сам, поэтому «Мой» — на любом числе пар).
 *
 * Свой текст и режим живут в `localStorage` этого браузера: при следующем открытии страницы они на
 * месте. Хранилище может быть недоступно (приватное окно, запрет) — тогда просто без памяти.
 *
 * Модель по умолчанию — Nano Banana 2.1 (07.10: вдвое дешевле 2 на выходе, 2 у Google deprecated);
 * нет её discovery — Nano Banana 2 (по пробам лучше остальных), нет и её — gpt-image-2.
 */
import type { GenParams, ModelOption } from "./fitting";

export type PromptMode = "auto" | "custom";
/** `ref` — референс, чей разбор сцены лежит в тексте (взят «из авто»); null — неизвестно или не брали. */
export type PromptState = { mode: PromptMode; text: string; ref: number | null };
export type PromptStore = Pick<Storage, "getItem" | "setItem">;

const MODE_KEY = "reels.prompt.mode";
const TEXT_KEY = "reels.prompt.custom";
const REF_KEY = "reels.prompt.ref";

export const NANO_BANANA_21 = "google/gemini-nano-banana-2.1";
export const NANO_BANANA = "google/gemini-3.1-flash-image";
const DEFAULT_MODELS = [NANO_BANANA_21, NANO_BANANA];
const FALLBACK_MODEL = "gpt-image-2";

export function loadPrompt(store: PromptStore | null): PromptState {
  try {
    const mode = store?.getItem(MODE_KEY);
    const ref = Number(store?.getItem(REF_KEY));
    return {
      mode: mode === "custom" ? "custom" : "auto",
      text: store?.getItem(TEXT_KEY) ?? "",
      ref: Number.isInteger(ref) && ref > 0 ? ref : null,
    };
  } catch {
    return { mode: "auto", text: "", ref: null };
  }
}

export function savePrompt(store: PromptStore | null, state: PromptState): void {
  try {
    store?.setItem(MODE_KEY, state.mode);
    store?.setItem(TEXT_KEY, state.text);
    store?.setItem(REF_KEY, state.ref === null ? "" : String(state.ref));
  } catch {
    // без памяти: страница работает и так
  }
}

/** `localStorage`, если он есть и доступен. */
export function browserStore(): PromptStore | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** Модель селектора по умолчанию: первая доступная из Nano Banana 2.1 и 2, иначе gpt-image-2. */
export function defaultModelCode(models: ModelOption[]): string {
  return DEFAULT_MODELS.find((code) => models.some((m) => m.code === code && !m.disabled)) ?? FALLBACK_MODEL;
}

/**
 * Параметры после выбора модели: поля прежней не переносим — у каждой свои качество, пропорции и n.
 * gpt-image-2 — `model: null` и всё из пресета; OpenRouter — умолчания модели, n с 1, пропорция по сцене.
 */
export function modelParams(value: GenParams, m: ModelOption): GenParams {
  if (m.provider === "openai") return { ...value, model: null, quality: null, size: null, n: null, aspect: null, resolution: null };
  const dflt = m.default_params;
  const q = typeof dflt.quality === "string" && m.quality?.includes(dflt.quality) ? dflt.quality : null;
  const res = typeof dflt.resolution === "string" && m.resolution?.includes(dflt.resolution) ? dflt.resolution : null;
  return { ...value, model: m.code, quality: q, size: null, n: 1, aspect: "auto", resolution: res };
}
